import SharedPreferences from "../../../constants/SharedPreferences";
import {
  getMinimumAllowedCorrectQuestions,
  isValidSecurityQuestionKNValues,
} from "../../../util/SecurityQuestionsValidationUtil";
import { getFromStorage } from "../../../util/Utility";
import Constants from "../../../constants/Constants";
import BayunError from "../../../constants/BayunError";

/**
 * Escapes a value for interpolation into HTML text or a quoted attribute.
 *
 * Security question text is authored by members and round-tripped through the server, so it is
 * untrusted input. It is interpolated into the innerHTML of the dialogs that collect the
 * passphrase and security answers, which means an unescaped `<img src=x onerror=...>` stored as a
 * question would execute next to that input and next to a non-HttpOnly session cookie.
 */
function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Exported for tests only; the escaping itself is an internal detail of this module. */
export const escapeHtmlForTest = escapeHtml;

function addScript(id, src, callback) {
  var s = document.createElement("script");
  s.setAttribute("src", src);
  s.setAttribute("id", id);
  s.onload = callback;
  document.head.appendChild(s);
}

/**
 * This method programatically adds a stylesheet to the body of the page.
 *
 * @param {String} url link of the stylesheet to be added.
 * @param {Function} callback callback to be called when the sheet is scuccessfully added.
 */
function linkStylesheet(url, callback) {
  var cssnode = document.createElement("link");

  cssnode.type = "text/css";
  cssnode.rel = "stylesheet";
  cssnode.href = url;

  cssnode.onreadystatechange = callback;
  cssnode.onload = callback;

  document.head.appendChild(cssnode);
}

function bindFormSubmitToDefaultAction(dialog, submitButtonSelector) {
  const form = dialog?.querySelector("form");
  if (!form) {
    return;
  }

  const triggerDefaultAction = () => {
    dialog.querySelector(submitButtonSelector)?.click();
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    triggerDefaultAction();
  });

  form.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const tagName = target.tagName;
    if (tagName !== "INPUT" && tagName !== "TEXTAREA") {
      return;
    }

    event.preventDefault();
    triggerDefaultAction();
  });
}

function getPasswordVisibilityToggleIconMarkup() {
  return '<span class="icon-eye-closed" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"></path><circle cx="12" cy="12" r="3"></circle></svg></span><span class="icon-eye-open" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M3 3l18 18"></path><path d="M10.6 10.7a3 3 0 0 0 4.2 4.2"></path><path d="M9.9 5.2A10.8 10.8 0 0 1 12 5c6.5 0 10 7 10 7a16.2 16.2 0 0 1-4.2 4.7"></path><path d="M6.4 6.4C3.8 8.2 2 12 2 12s3.5 6 10 6c1.7 0 3.2-.4 4.5-1"></path></svg></span>';
}

const DIALOG_BUTTON_LOADING_CSS = `
  .dialog-actions button.is-loading {
    position: relative;
    color: transparent;
    cursor: default;
    pointer-events: none;
  }
  .dialog-actions button.is-loading:hover {
    background: #1976d2;
  }
  .dialog-actions button:disabled {
    opacity: 0.85;
    pointer-events: none;
    cursor: not-allowed;
  }
  .dialog-actions button.is-loading::after {
    content: "";
    position: absolute;
    width: 16px;
    height: 16px;
    top: 50%;
    left: 50%;
    margin: -8px 0 0 -8px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: two-fa-dialog-btn-spin 0.7s linear infinite;
  }
  @keyframes two-fa-dialog-btn-spin {
    to { transform: rotate(360deg); }
  }
`;

function setDialogButtonLoading(button, isLoading, companionButton) {
  if (!button) {
    return;
  }

  if (isLoading) {
    button.classList.add("is-loading");
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
  } else {
    button.classList.remove("is-loading");
    button.disabled = false;
    button.removeAttribute("aria-busy");
  }

  if (companionButton) {
    companionButton.disabled = isLoading;
  }
}

function wrapFailureCallback(failureCallback, onFailure) {
  return (error) => {
    onFailure?.(error);
    failureCallback(error);
  };
}

function getFailureMessage(error) {
  if (typeof error === "string") {
    return error;
  }

  return error?.message || BayunError.ERROR_SOMETHING_WENT_WRONG;
}

/**
 * Credential setup already finished (or cannot be retried) — e.g. member is
 * pending approval. Retrying Skip/Ok would call setNewUserCredentials again and
 * surface a misleading "Something Went Wrong".
 */
export const isNonRetryableCredentialSetupErrorForTest = (error) =>
  isNonRetryableCredentialSetupError(error);

function isNonRetryableCredentialSetupError(error) {
  const message = getFailureMessage(error);
  return [
    BayunError.ERROR_MEMBER_NOT_APPROVED_YET,
    BayunError.ERROR_MEMBER_APP_NOT_APPROVED,
    BayunError.ERROR_REGISTRATION_FAILED_APPLICATION_IS_NOT_APPROVED,
    BayunError.USER_REGISTRATION_PENDING,
    BayunError.ERROR_LINK_USER_MEMBER_ON_ADMIN,
    BayunError.ERROR_ORG_OR_MEMBER_OR_MEMBERAPP_ALREADY_EXIST,
    BayunError.ERROR_MEMBER_ALREADY_EXISTS,
    BayunError.ERROR_ORGANIZATION_ALREADY_EXISTS,
    BayunError.ERROR_MEMBER_ALREADY_EXISTS_FOR_A_DIFFERENT_EMAIL_ADDRESS,
    BayunError.ERROR_APP_NOT_LINKED,
    BayunError.ERROR_USER_ALREADY_EXISTS,
    BayunError.APP_DOES_NOT_HAVE_THE_CORRECT_ROLE,
  ].includes(message);
}

function bindPasswordVisibilityToggle(dialog) {
  dialog?.addEventListener("click", (event) => {
    const toggleButton = event.target?.closest(".password-visibility-toggle");
    if (!toggleButton) {
      return;
    }

    const targetInputId = toggleButton.getAttribute("data-target-id");
    const targetInput = targetInputId
      ? document.getElementById(targetInputId)
      : null;
    if (!targetInput) {
      return;
    }

    const shouldShow = targetInput.type === "password";
    targetInput.type = shouldShow ? "text" : "password";
    toggleButton.classList.toggle("is-visible", shouldShow);
    toggleButton.setAttribute("aria-pressed", String(shouldShow));
    toggleButton.setAttribute(
      "aria-label",
      `${shouldShow ? "Hide" : "Show"} ${targetInput.placeholder.toLowerCase()}`,
    );
  });
}

const isPassphraseActiveFlag = (value) =>
  value === true ||
  value === "true" ||
  (typeof value === "string" && value.toLowerCase() === "true");

const FORGOT_CREDENTIALS_TOOLTIP =
  "Use your recovery-key to unlock your account via BayunAdmin";

function getBayunAdminLoginUrl(email) {
  const isSdkOnLocalHost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const portalBase = isSdkOnLocalHost
    ? "http://localhost:3000/"
    : Constants.DEV_PORTAL_BASE_URL || "http://localhost:3000/";
  let loginUrl = "";
  try {
    const url = new URL(portalBase, window.location.href);
    const isLocalHost =
      url.hostname === "localhost" || url.hostname === "127.0.0.1";
    if (isLocalHost) {
      const port = url.port || "3000";
      loginUrl = `${url.protocol}//${url.hostname}:${port}/recover-credentials`;
    } else {
      loginUrl = `${url.origin}/dev-portal/recover-credentials`;
    }
  } catch {
    loginUrl = "http://localhost:3000/recover-credentials";
  }

  const trimmedEmail = typeof email === "string" ? email.trim() : "";
  if (!trimmedEmail) {
    return loginUrl;
  }
  return `${loginUrl}?email=${encodeURIComponent(trimmedEmail)}`;
}

function getForgotCredentialsLinkMarkup(email) {
  const loginUrl = getBayunAdminLoginUrl(email);
  return `
    <div class="forgot-credentials-row">
      <a
        class="forgot-credentials-link"
        href="${loginUrl}"
        target="_blank"
        rel="noopener noreferrer"
        data-tooltip="${FORGOT_CREDENTIALS_TOOLTIP}"
      >Forgot Credentials?</a>
    </div>
  `;
}

/**
 * This method dynamically creates a dialog to take input of passphrase or security question.
 *
 * @param {String} sessionId id of the session in question.
 * @param {List} questionsList list of objects with question id and text for the security questions.
 * @param {boolean} isPassphraseActive flag is passphrase is active for a user.
 * @param {String} passcode passcode for the member.
 * @param {Function} successCallback callback is called when authentication is successful.
 * @param {Function} failureCallback callback is called if any error occurs in the authentication.
 */
export async function validateSecurityQuestionsPassphraseDialog(
  sessionId,
  bayunCore,
  questionsList,
  isPassphraseActive,
  minimumCorrectQuestionsRequired,
  passcode,
  authorizeMemberCallback,
  successCallback,
  failureCallback,
  userRecoveryStatus = null,
) {
  document.getElementById("security-questions-dialog")?.remove();
  document.getElementById("passphrase-dialog")?.remove();
  document.getElementById("recovery-mode-dialog")?.remove();
  document.getElementById("two-fa-dialog-css")?.remove();

  const style = document.createElement("style");
  style.setAttribute("id", "two-fa-dialog-css");
  style.innerHTML = `
    #passphrase-dialog, #security-questions-dialog, #recovery-mode-dialog {
      width: min(560px, calc(100vw - 2rem));
      border: none;
      border-radius: 12px;
      padding: 0;
      box-shadow: 0 10px 38px rgba(0, 0, 0, 0.3), 0 8px 12px rgba(0, 0, 0, 0.22);
      color: #1f2937;
      font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    }
    #passphrase-dialog::backdrop, #security-questions-dialog::backdrop, #recovery-mode-dialog::backdrop {
      background: rgba(15, 23, 42, 0.5);
    }
    .dialog-content {
      padding: 20px 24px 18px;
    }
    .dialog-title {
      margin: 0 0 6px;
      font-size: 1.25rem;
      font-weight: 500;
      line-height: 1.4;
      letter-spacing: 0.01em;
    }
    #passphrase-dialog .register-flow-info,
    #security-questions-dialog .register-flow-info {
      margin: 0 0 16px;
      padding: 12px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      color: #334155;
      font-size: 0.9rem;
      line-height: 1.4;
    }
    #passphrase-dialog input[type="password"],
    #passphrase-dialog input[type="text"] {
      width: 100%;
      height: 40px;
      padding: 0 12px;
      box-sizing: border-box;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      font-size: 0.95rem;
      line-height: 1.4;
      outline: none;
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }
    #passphrase-dialog .password-visibility-input-wrap,
    #security-questions-dialog .password-visibility-input-wrap {
      position: relative;
      margin-top: 6px;
    }
    #passphrase-dialog .password-visibility-toggle,
    #security-questions-dialog .password-visibility-toggle {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 32px;
      height: 32px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      background: #f8fafc;
      color: #334155;
      cursor: pointer;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    #passphrase-dialog .password-visibility-toggle:hover,
    #security-questions-dialog .password-visibility-toggle:hover {
      background: #eef2f7;
    }
    #passphrase-dialog .password-visibility-toggle:focus-visible,
    #security-questions-dialog .password-visibility-toggle:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.2);
    }
    #passphrase-dialog .password-visibility-toggle .icon-eye-closed,
    #security-questions-dialog .password-visibility-toggle .icon-eye-closed {
      display: inline;
    }
    #passphrase-dialog .password-visibility-toggle .icon-eye-open,
    #security-questions-dialog .password-visibility-toggle .icon-eye-open {
      display: none;
    }
    #passphrase-dialog .password-visibility-toggle.is-visible .icon-eye-closed,
    #security-questions-dialog .password-visibility-toggle.is-visible .icon-eye-closed {
      display: none;
    }
    #passphrase-dialog .password-visibility-toggle.is-visible .icon-eye-open,
    #security-questions-dialog .password-visibility-toggle.is-visible .icon-eye-open {
      display: inline;
    }
    #passphrase-dialog .password-visibility-toggle svg,
    #security-questions-dialog .password-visibility-toggle svg {
      width: 16px;
      height: 16px;
      stroke: #334155;
      fill: none;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    #passphrase-dialog fieldset,
    #security-questions-dialog fieldset {
      border: none;
      padding: 0;
      margin: 0;
    }
    #security-questions-dialog fieldset > div {
      margin-top: 14px;
    }
    #security-questions-dialog fieldset > div:first-child {
      margin-top: 8px;
    }
    #security-questions-dialog input[type="password"],
    #security-questions-dialog input[type="text"] {
      margin-top: 6px;
      width: 100%;
      height: 40px;
      padding: 0 12px;
      box-sizing: border-box;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      font-size: 0.95rem;
      line-height: 1.4;
      outline: none;
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }
    #passphrase-dialog .password-visibility-input-wrap input[type="password"],
    #passphrase-dialog .password-visibility-input-wrap input[type="text"],
    #security-questions-dialog .password-visibility-input-wrap input[type="password"],
    #security-questions-dialog .password-visibility-input-wrap input[type="text"] {
      margin-top: 0;
      padding-right: 44px;
    }
    #security-questions-dialog label {
      display: block;
      color: #334155;
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.3;
    }
    #passphrase-dialog input[type="password"]:focus,
    #passphrase-dialog input[type="text"]:focus,
    #security-questions-dialog input[type="password"]:focus,
    #security-questions-dialog input[type="text"]:focus {
      border-color: #1976d2;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
    }
    .validateTips {
      display: none;
      margin: 8px 0 4px;
      min-height: 1.1rem;
      padding: 10px 12px;
      border: 1px solid transparent;
      border-radius: 8px;
      color: #b00020;
      font-size: 0.875rem;
      line-height: 1.35;
    }
    .validateTips.ui-state-error {
      border-color: #f5c2c7;
      background: #f8d7da;
    }
    .dialog-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 20px;
      flex-wrap: wrap;
    }
    .dialog-actions button {
      min-width: 108px;
      height: 36px;
      padding: 0 14px;
      cursor: pointer;
      border-radius: 8px;
      border: none;
      background: #1976d2;
      color: #ffffff;
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.01em;
      transition: background-color 120ms ease, box-shadow 120ms ease;
    }
    .dialog-actions button:hover {
      background: #1565c0;
    }
    .dialog-actions button:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.25);
    }
    .dialog-actions #switch-to-passphrase-btn,
    .dialog-actions #switch-to-security-questions-btn,
    .dialog-actions .dialog-alternate-btn {
      background: #e3f2fd;
      color: #1565c0;
      border: 1px solid #bbdefb;
    }
    .dialog-actions #switch-to-passphrase-btn:hover,
    .dialog-actions #switch-to-security-questions-btn:hover,
    .dialog-actions .dialog-alternate-btn:hover {
      background: #d6eafc;
    }
    .forgot-credentials-row {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 14px;
    }
    .forgot-credentials-link {
      position: relative;
      color: #1565c0;
      font-size: 0.875rem;
      font-weight: 600;
      text-decoration: none;
      cursor: pointer;
    }
    .forgot-credentials-link:hover {
      text-decoration: underline;
    }
    .forgot-credentials-link:hover::after,
    .forgot-credentials-link:focus-visible::after {
      content: attr(data-tooltip);
      position: absolute;
      left: 50%;
      bottom: calc(100% + 8px);
      transform: translateX(-50%);
      width: max-content;
      max-width: 240px;
      padding: 8px 10px;
      border-radius: 8px;
      background: #0f172a;
      color: #ffffff;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 1.35;
      text-decoration: none;
      white-space: normal;
      z-index: 2;
      box-shadow: 0 8px 20px rgba(15, 23, 42, 0.25);
    }
    ${DIALOG_BUTTON_LOADING_CSS}
    @media (max-width: 600px) {
      .dialog-content {
        padding: 16px;
      }
      .dialog-actions button {
        flex: 1 1 100%;
      }
    }
  `;
  document.head.appendChild(style);

  const isRecoveryModeActive =
    userRecoveryStatus === Constants.USER_RECOVERY_STATUS.RECOVERY_STARTED ||
    userRecoveryStatus === Constants.USER_RECOVERY_STATUS.SET_CREDENTIALS;
  if (isRecoveryModeActive) {
    const recoveryModeDialog = document.createElement("dialog");
    recoveryModeDialog.id = "recovery-mode-dialog";
    recoveryModeDialog.innerHTML = `
      <div class="dialog-content">
        <h3 class="dialog-title">Account Recovery In Progress</h3>
        <p class="register-flow-info">
          This account is currently in recovery mode. Please use the Bayun Admin Panel to complete account recovery.
        </p>
        <div class="dialog-actions">
          <button type="button" id="close-recovery-mode-dialog">Close</button>
        </div>
      </div>
    `;
    document.body.appendChild(recoveryModeDialog);
    let recoveryModeDismissed = false;
    const dismissRecoveryModeDialog = () => {
      if (recoveryModeDismissed) {
        return;
      }
      recoveryModeDismissed = true;
      recoveryModeDialog.close();
      recoveryModeDialog.remove();
      document.getElementById("two-fa-dialog-css")?.remove();
      if (typeof failureCallback === "function") {
        failureCallback(BayunError.ERROR_ACCOUNT_IN_RECOVERY_MODE);
      }
    };
    recoveryModeDialog
      .querySelector("#close-recovery-mode-dialog")
      ?.addEventListener("click", dismissRecoveryModeDialog);
    recoveryModeDialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      dismissRecoveryModeDialog();
    });
    recoveryModeDialog.showModal();
    return;
  }

  let registerFlowInfoText = "";
  const isRegisterFlowValue = await getFromStorage(
    sessionId,
    SharedPreferences.IS_REGISTER_FLOW,
  );
  const isRegisterFlow =
    isRegisterFlowValue === true || isRegisterFlowValue === "true";
  registerFlowInfoText = isRegisterFlow
    ? '<div class="register-flow-info">Validate existing User account with this email in order to link the new Member account being registered</div>'
    : "";

  const storedPassphraseActive = await getFromStorage(
    sessionId,
    SharedPreferences.IS_PASSPHRASE_ACTIVE,
  );
  const storedEmail = await getFromStorage(
    sessionId,
    SharedPreferences.EMAIL_ADDRESS,
  );
  const forgotCredentialsEmail =
    typeof storedEmail === "string" ? storedEmail.trim() : "";
  const normalizedQuestions = Array.isArray(questionsList)
    ? questionsList.slice()
    : [];
  const hasSecurityQuestions = normalizedQuestions.length > 0;
  const isPassphraseEnabled =
    isPassphraseActiveFlag(isPassphraseActive) ||
    isPassphraseActiveFlag(storedPassphraseActive);

  const passphraseDialog = document.createElement("dialog");
  passphraseDialog.id = "passphrase-dialog";
  passphraseDialog.innerHTML = `
    <div class="dialog-content">
      <h3 class="dialog-title">Validate Passphrase</h3>
      <span class="validateTips"></span>
      ${registerFlowInfoText}
      <form>
        <fieldset id="formFieldSet">
          <div id="passphraseBlock">
            <div class="password-visibility-input-wrap">
              <input type="password" placeholder="passphrase" name="bayunPassphrase" id="bayunPassphrase" autocomplete="current-password">
              <button type="button" class="password-visibility-toggle" data-target-id="bayunPassphrase" aria-label="Show passphrase" aria-pressed="false">${getPasswordVisibilityToggleIconMarkup()}</button>
            </div>
          </div>
        </fieldset>
      </form>
      <div class="dialog-actions">
        ${hasSecurityQuestions
      ? '<button type="button" class="dialog-alternate-btn" id="switch-to-security-questions-btn">Validate Security Questions</button>'
      : ""
    }
        <button type="button" id="validate-passphrase-ok">Ok</button>
      </div>
    </div>
  `;

  const securityQuestionRows = normalizedQuestions
    .map(
      (question, index) =>
        `<div id="bayunSecQuesBlock${escapeHtml(question.questionId)}"><label for="bayunSecQues${escapeHtml(question.questionId)}">${escapeHtml(question.questionText)}</label><div class="password-visibility-input-wrap"><input type="password" name="bayunSecQues${index}" id="bayunSecQues${index}" autocomplete="off"><button type="button" class="password-visibility-toggle" data-target-id="bayunSecQues${index}" aria-label="Show answer ${index + 1}" aria-pressed="false">${getPasswordVisibilityToggleIconMarkup()}</button></div></div>`,
    )
    .join("");

  const securityQuestionsDialog = document.createElement("dialog");
  securityQuestionsDialog.id = "security-questions-dialog";
  securityQuestionsDialog.innerHTML = `
    <div class="dialog-content">
      <h3 class="dialog-title">Validate Security Questions</h3>
      <span class="validateTips"></span>
      ${registerFlowInfoText}
      <form>
        <fieldset id="sqFormFieldSet">
          ${securityQuestionRows}
        </fieldset>
      </form>
      <div class="dialog-actions">
        ${isPassphraseEnabled
      ? '<button type="button" class="dialog-alternate-btn" id="switch-to-passphrase-btn">Validate Passphrase</button>'
      : ""
    }
        <button type="button" id="validate-security-questions-ok">Ok</button>
      </div>
      ${getForgotCredentialsLinkMarkup(forgotCredentialsEmail)}
    </div>
  `;

  document.body.appendChild(passphraseDialog);
  document.body.appendChild(securityQuestionsDialog);

  bindFormSubmitToDefaultAction(passphraseDialog, "#validate-passphrase-ok");
  bindFormSubmitToDefaultAction(
    securityQuestionsDialog,
    "#validate-security-questions-ok",
  );
  bindPasswordVisibilityToggle(passphraseDialog);
  bindPasswordVisibilityToggle(securityQuestionsDialog);

  let dismissOnlyMode = false;

  const closeValidationDialogs = () => {
    passphraseDialog.close();
    securityQuestionsDialog.close();
    passphraseDialog.remove();
    securityQuestionsDialog.remove();
    document.getElementById("two-fa-dialog-css")?.remove();
  };

  const enterDismissOnlyMode = () => {
    dismissOnlyMode = true;
    [passphraseDialog, securityQuestionsDialog].forEach((dialog) => {
      const okButton = dialog.querySelector(
        "#validate-passphrase-ok, #validate-security-questions-ok",
      );
      const switchButton = dialog.querySelector(
        "#switch-to-security-questions-btn, #switch-to-passphrase-btn",
      );
      if (okButton) {
        okButton.textContent = "Close";
        okButton.disabled = false;
        okButton.classList.remove("is-loading");
        okButton.removeAttribute("aria-busy");
      }
      if (switchButton) {
        switchButton.style.display = "none";
      }
      const fieldset = dialog.querySelector("fieldset");
      if (fieldset) {
        fieldset.style.display = "none";
      }
    });
  };

  passphraseDialog.addEventListener("cancel", (event) => {
    if (dismissOnlyMode) {
      event.preventDefault();
      closeValidationDialogs();
    }
  });

  securityQuestionsDialog.addEventListener("cancel", (event) => {
    if (dismissOnlyMode) {
      event.preventDefault();
      closeValidationDialogs();
    }
  });

  const updateError = (errorMessage) => {
    const activeDialog = securityQuestionsDialog.open
      ? securityQuestionsDialog
      : passphraseDialog;
    const tips = activeDialog.querySelector(".validateTips");
    if (tips) {
      tips.classList.add("ui-state-error");
      tips.style.display = "block";
      tips.textContent = errorMessage;
    }
  };

  const handleValidationFailure = (error) => {
    updateError(getFailureMessage(error));
    if (isNonRetryableCredentialSetupError(error)) {
      enterDismissOnlyMode();
    }
  };

  const removeError = () => {
    [securityQuestionsDialog, passphraseDialog].forEach((dialog) => {
      const tips = dialog.querySelector(".validateTips");
      if (tips) {
        tips.classList.remove("ui-state-error");
        tips.style.display = "none";
        tips.textContent = "";
      }
    });
  };

  const updateForSecurityQuestions = () => {
    if (!hasSecurityQuestions) {
      if (isPassphraseEnabled) {
        updateForPassphrase();
      } else {
        removeError();
        passphraseDialog.close();
        securityQuestionsDialog.showModal();
      }
      return;
    }
    removeError();
    for (let i = 0; i < normalizedQuestions.length; i++) {
      const input = document.getElementById("bayunSecQues" + i);
      if (input) {
        input.value = "";
      }
    }
    passphraseDialog.close();
    securityQuestionsDialog.showModal();
  };

  const updateForPassphrase = () => {
    removeError();
    const passphraseInput = document.getElementById("bayunPassphrase");
    if (passphraseInput) {
      passphraseInput.value = "";
    }
    securityQuestionsDialog.close();
    passphraseDialog.showModal();
  };

  const validatePassphraseOkButton = passphraseDialog.querySelector(
    "#validate-passphrase-ok",
  );
  validatePassphraseOkButton?.addEventListener("click", async () => {
    if (dismissOnlyMode) {
      closeValidationDialogs();
      return;
    }
    removeError();
    const passphrase = document
      .getElementById("bayunPassphrase")
      ?.value.trim();
    if (!passphrase) {
      updateError("Passphrase cannot be empty");
      return;
    }

    let validationFailed = false;
    setDialogButtonLoading(validatePassphraseOkButton, true);
    try {
      await bayunCore.validatePassphrase({
        sessionId: sessionId,
        passphrase: passphrase,
        authorizeMemberCallback: authorizeMemberCallback,
        successCallback: successCallback,
        failureCallback: wrapFailureCallback(failureCallback, (error) => {
          validationFailed = true;
          handleValidationFailure(error);
        }),
      });
    } catch (error) {
      if (!validationFailed) {
        validationFailed = true;
        handleValidationFailure(error);
        failureCallback(error);
      }
    } finally {
      if (!dismissOnlyMode) {
        setDialogButtonLoading(validatePassphraseOkButton, false);
      }
    }

    if (!validationFailed) {
      const passphraseInput = document.getElementById("bayunPassphrase");
      if (passphraseInput) {
        passphraseInput.value = "";
      }
      passphraseDialog.close();
    }
  });

  passphraseDialog
    .querySelector("#switch-to-security-questions-btn")
    ?.addEventListener("click", () => {
      if (dismissOnlyMode) {
        return;
      }
      updateForSecurityQuestions();
    });

  securityQuestionsDialog
    .querySelector("#switch-to-passphrase-btn")
    ?.addEventListener("click", () => {
      if (dismissOnlyMode) {
        return;
      }
      updateForPassphrase();
    });

  securityQuestionsDialog.querySelectorAll(".forgot-credentials-link").forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const recoverUrl =
        event.currentTarget.getAttribute("href") ||
        getBayunAdminLoginUrl(forgotCredentialsEmail);
      window.open(recoverUrl, "_blank", "noopener,noreferrer");
    });
  });

  const validateSecurityQuestionsOkButton = securityQuestionsDialog.querySelector(
    "#validate-security-questions-ok",
  );
  validateSecurityQuestionsOkButton?.addEventListener("click", async () => {
    if (dismissOnlyMode) {
      closeValidationDialogs();
      return;
    }
    if (!hasSecurityQuestions) {
      updateError("Security questions are not available");
      return;
    }
    removeError();
    let count = 0;
    const answersList = [];

    for (let i = 0; i < normalizedQuestions.length; i++) {
      const answer = document.getElementById("bayunSecQues" + i)?.value.trim();
      if (answer) {
        count++;
        answersList.push({
          questionId: normalizedQuestions[i].questionId,
          answer: answer,
        });
      }
    }

    if (count < minimumCorrectQuestionsRequired) {
      updateError(
        `Please answer at least ${minimumCorrectQuestionsRequired} questions`,
      );
      return;
    }

    let validationFailed = false;
    setDialogButtonLoading(validateSecurityQuestionsOkButton, true);
    try {
      await bayunCore.validateSecurityQuestions({
        sessionId: sessionId,
        answers: answersList,
        authorizeMemberCallback: authorizeMemberCallback,
        successCallback: successCallback,
        failureCallback: wrapFailureCallback(failureCallback, (error) => {
          validationFailed = true;
          handleValidationFailure(error);
        }),
      });
    } catch (error) {
      if (!validationFailed) {
        validationFailed = true;
        handleValidationFailure(error);
        failureCallback(error);
      }
    } finally {
      if (!dismissOnlyMode) {
        setDialogButtonLoading(validateSecurityQuestionsOkButton, false);
      }
    }

    if (!validationFailed) {
      securityQuestionsDialog.close();
    }
  });

  if (isPassphraseEnabled) {
    updateForPassphrase();
  } else {
    updateForSecurityQuestions();
  }


}

export function setSecurityQuestionsPassphraseDialog(
  bayunCore,
  sessionId,
  email,
  authorizeMemberCallback,
  successCallback,
  failureCallback,
  enableFaceRecognition,
) {
  document.getElementById("set-passphrase-dialog")?.remove();
  document.getElementById("set-security-questions-dialog")?.remove();
  document.getElementById("two-fa-dialog-css")?.remove();

  const style = document.createElement("style");
  style.setAttribute("id", "two-fa-dialog-css");
  style.innerHTML = `
    #set-passphrase-dialog, #set-security-questions-dialog {
      width: min(560px, calc(100vw - 2rem));
      border: none;
      border-radius: 12px;
      padding: 0;
      box-shadow: 0 10px 38px rgba(0, 0, 0, 0.3), 0 8px 12px rgba(0, 0, 0, 0.22);
      color: #1f2937;
      font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    }
    #set-passphrase-dialog::backdrop, #set-security-questions-dialog::backdrop {
      background: rgba(15, 23, 42, 0.5);
    }
    .dialog-content {
      padding: 20px 24px 18px;
    }
    .dialog-title {
      margin: 0 0 6px;
      font-size: 1.25rem;
      font-weight: 500;
      line-height: 1.4;
      letter-spacing: 0.01em;
    }
    #set-passphrase-dialog .dialog-description,
    #set-security-questions-dialog .dialog-description {
      margin: 0 0 16px;
      padding: 12px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      color: #334155;
      font-size: 0.9rem;
      line-height: 1.4;
    }
    #set-passphrase-dialog input[type="password"],
    #set-passphrase-dialog input[type="text"] {
      width: 100%;
      height: 40px;
      padding: 0 12px;
      box-sizing: border-box;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      font-size: 0.95rem;
      line-height: 1.4;
      outline: none;
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }
    #set-passphrase-dialog fieldset,
    #set-security-questions-dialog fieldset {
      border: none;
      padding: 0;
      margin: 0;
    }
    #set-passphraseBlock input {
      margin: 8px 0;
    }
    #set-security-questions-dialog fieldset > div {
      margin-top: 14px;
    }
    #set-security-questions-dialog fieldset > div:first-child {
      margin-top: 8px;
    }
    #set-security-questions-dialog input:not([type="range"]) {
      margin-top: 6px;
      width: 100%;
      height: 40px;
      padding: 0 12px;
      box-sizing: border-box;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      font-size: 0.95rem;
      line-height: 1.4;
      outline: none;
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }
    #set-security-questions-dialog .answer-input-wrap {
      position: relative;
      margin-top: 6px;
    }
    #set-security-questions-dialog .answer-input-wrap input {
      margin-top: 0;
      padding-right: 72px;
    }
    #set-security-questions-dialog .sq-answer-visibility-toggle {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 32px;
      height: 32px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      background: #f8fafc;
      color: #334155;
      cursor: pointer;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    #set-security-questions-dialog .sq-answer-visibility-toggle:hover {
      background: #eef2f7;
    }
    #set-security-questions-dialog .sq-answer-visibility-toggle:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.2);
    }
    #set-security-questions-dialog .sq-answer-visibility-toggle .icon-eye-closed {
      display: inline;
    }
    #set-security-questions-dialog .sq-answer-visibility-toggle .icon-eye-open {
      display: none;
    }
    #set-security-questions-dialog .sq-answer-visibility-toggle.is-visible .icon-eye-closed {
      display: none;
    }
    #set-security-questions-dialog .sq-answer-visibility-toggle.is-visible .icon-eye-open {
      display: inline;
    }
    #set-security-questions-dialog .sq-answer-visibility-toggle svg {
      width: 16px;
      height: 16px;
      stroke: #334155;
      fill: none;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    #set-passphrase-dialog .password-visibility-input-wrap {
      position: relative;
      margin: 8px 0;
    }
    #set-passphrase-dialog .password-visibility-input-wrap input {
      margin: 0;
      padding-right: 44px;
    }
    #set-passphrase-dialog .password-visibility-toggle {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 32px;
      height: 32px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      background: #f8fafc;
      color: #334155;
      cursor: pointer;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    #set-passphrase-dialog .password-visibility-toggle:hover {
      background: #eef2f7;
    }
    #set-passphrase-dialog .password-visibility-toggle:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.2);
    }
    #set-passphrase-dialog .password-visibility-toggle .icon-eye-closed {
      display: inline;
    }
    #set-passphrase-dialog .password-visibility-toggle .icon-eye-open {
      display: none;
    }
    #set-passphrase-dialog .password-visibility-toggle.is-visible .icon-eye-closed {
      display: none;
    }
    #set-passphrase-dialog .password-visibility-toggle.is-visible .icon-eye-open {
      display: inline;
    }
    #set-passphrase-dialog .password-visibility-toggle svg {
      width: 16px;
      height: 16px;
      stroke: #334155;
      fill: none;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    #set-security-questions-dialog label {
      display: block;
      color: #334155;
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.3;
    }
    #set-passphrase-dialog input[type="password"]:focus,
    #set-passphrase-dialog input[type="text"]:focus,
    #set-security-questions-dialog input:not([type="range"]):focus {
      border-color: #1976d2;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
    }
    .dialog-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 20px;
      flex-wrap: wrap;
    }
    .dialog-actions button {
      min-width: 108px;
      height: 36px;
      padding: 0 14px;
      cursor: pointer;
      border-radius: 8px;
      border: none;
      background: #1976d2;
      color: #ffffff;
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.01em;
      transition: background-color 120ms ease, box-shadow 120ms ease;
    }
    .dialog-actions button:hover {
      background: #1565c0;
    }
    .dialog-actions button:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.25);
    }
    .dialog-actions #set-passphrase-skip {
      background: #e3f2fd;
      color: #1565c0;
      border: 1px solid #bbdefb;
    }
    .dialog-actions #set-passphrase-skip:hover {
      background: #d6eafc;
    }
    .dialog-actions #set-passphrase-skip.is-loading,
    .dialog-actions #set-passphrase-skip.is-loading:hover {
      background: #e3f2fd;
    }
    .dialog-actions #set-passphrase-skip.is-loading::after {
      border-color: rgba(21, 101, 192, 0.3);
      border-top-color: #1565c0;
    }
    ${DIALOG_BUTTON_LOADING_CSS}
    .validateTips {
      display: none;
      margin: 8px 0 4px;
      min-height: 1.1rem;
      padding: 10px 12px;
      border: 1px solid transparent;
      border-radius: 8px;
      color: #b00020;
      font-size: 0.875rem;
      line-height: 1.35;
    }
    .validateTips.ui-state-error {
      border-color: #f5c2c7;
      background: #f8d7da;
    }
    .enable-fr-wrap {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 1rem;
    }
    .enable-fr-wrap input[type="checkbox"] {
      width: 20px;
      margin: 0;
      padding: 0;
    }
    .slider-wrap {
      width:100%
    }
    .range-slider {
      --range-thumb-size: 18px;
      --range-thumb-inset: 9px;
      --range-track-height: 6px;
      position: relative;
      width: 100%;
      margin-top: 6px;
      height: 28px;
    }
    .range-slider-track {
      position: absolute;
      left: var(--range-thumb-inset);
      right: var(--range-thumb-inset);
      top: 50%;
      transform: translateY(-50%);
      height: var(--range-track-height);
      border-radius: 3px;
      background: #e2e8f0;
      pointer-events: none;
      z-index: 0;
      overflow: hidden;
    }
    .range-slider-fill {
      height: 100%;
      width: 0;
      border-radius: 3px;
      background: #2563eb;
      transition: width 80ms ease;
    }
    .range-slider-marks {
      position: absolute;
      left: var(--range-thumb-inset);
      right: var(--range-thumb-inset);
      top: 50%;
      transform: translateY(-50%);
      height: 0;
      pointer-events: none;
      z-index: 2;
    }
    .range-slider-mark {
      position: absolute;
      top: 0;
      width: 8px;
      height: 8px;
      margin-left: -4px;
      margin-top: -4px;
      border-radius: 50%;
      background: #475569;
      border: 2px solid #ffffff;
      box-sizing: border-box;
    }
    .range-slider input[type="range"] {
      position: relative;
      z-index: 1;
      width: 100%;
      height: 28px;
      margin: 0;
      padding: 0;
      border: none;
      box-shadow: none;
      outline: none;
      background: transparent;
      -webkit-appearance: none;
      appearance: none;
      cursor: pointer;
    }
    .range-slider input[type="range"]::-webkit-slider-runnable-track {
      height: var(--range-track-height);
      background: transparent;
    }
    .range-slider input[type="range"]::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: var(--range-thumb-size);
      height: var(--range-thumb-size);
      margin-top: calc((var(--range-track-height) - var(--range-thumb-size)) / 2);
      border-radius: 50%;
      border: 2px solid #2563eb;
      background: #ffffff;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
      cursor: pointer;
    }
    .range-slider input[type="range"]::-moz-range-track {
      height: var(--range-track-height);
      background: transparent;
      border: none;
    }
    .range-slider input[type="range"]::-moz-range-thumb {
      width: var(--range-thumb-size);
      height: var(--range-thumb-size);
      border-radius: 50%;
      border: 2px solid #2563eb;
      background: #ffffff;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
      cursor: pointer;
    }
    .slider-wrap select {
      width: 100%;
      margin-top: 6px;
      padding: 8px 10px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      background: #ffffff;
    }
     .flex-row {
      display: flex;
      flex-direction: row;
      gap: 1rem;
     }
     .flex-row .slider-wrap:first-child {
      flex: 0 0 30%;
     }
     .flex-row .slider-wrap:last-child {
      flex: 1 1 70%;
     }
      .flex-column {
      display: flex;
      flex-direction: column;
      gap: 1rem;
     }
    @media (max-width: 600px) {
      .dialog-content {
        padding: 16px;
      }
      .dialog-actions button {
        flex: 1 1 100%;
      }
    }
  `;
  document.head.appendChild(style);

  let answersList = [];
  let registerFaceId = false;
  let selectedN = 5;
  let selectedK = 3;

  const passphraseDialog = document.createElement("dialog");
  passphraseDialog.id = "set-passphrase-dialog";
  passphraseDialog.innerHTML = `
    <div class="dialog-content">
      <h3 class="dialog-title">Create Passphrase</h3>
      <div class="dialog-description">
        This optional passphrase allows more convenient access to your account without having to enter multiple answers to your security questions every time you login from a new device. Choose a strong passphrase that you will not forget, but will be very hard for others to guess. Otherwise its better to skip this step, and you can still rely on your security questions you just created for logging in.
      </div>
      <span class="validateTips"></span>
      <form>
        <fieldset id="set-formFieldSet">
          <div id="set-passphraseBlock">
            <div class="password-visibility-input-wrap">
              <input type="password" placeholder="passphrase" name="bayunPassphrase" id="set-bayunPassphrase" autocomplete="new-password">
              <button type="button" class="password-visibility-toggle" data-target-id="set-bayunPassphrase" aria-label="Show passphrase" aria-pressed="false">${getPasswordVisibilityToggleIconMarkup()}</button>
            </div>
            <div class="password-visibility-input-wrap">
              <input type="password" placeholder="confirm passphrase" name="bayunPassphraseConfirm" id="set-bayunPassphraseConfirm" autocomplete="new-password">
              <button type="button" class="password-visibility-toggle" data-target-id="set-bayunPassphraseConfirm" aria-label="Show confirm passphrase" aria-pressed="false">${getPasswordVisibilityToggleIconMarkup()}</button>
            </div>
          </div>
        </fieldset>
      </form>
      <div class="dialog-actions">
        <button type="button" id="set-passphrase-skip">Skip</button>
        <button type="button" id="set-passphrase-ok">Ok</button>
      </div>
    </div>
  `;

  const securityQuestionsDialog = document.createElement("dialog");
  securityQuestionsDialog.id = "set-security-questions-dialog";
  securityQuestionsDialog.innerHTML = `
    <div class="dialog-content">
      <h3 class="dialog-title">Create Security Questions for Account Recovery</h3>
      <div class="dialog-description">
        Important: these answers are intermingled to create a master key for your account that protects all your data, such that you have exclusive control of your data. Forgetting <span id="set-forgetting-answers-value">${selectedN - selectedK + 1}</span> answers can permanently lock you out and make your data unrecoverable, so choose carefully!
      </div>
      <span class="validateTips"></span>
      <form>
        <fieldset id="set-sqFormFieldSet">
        <div class="flex-row">
        <div class="slider-wrap">
            <label for="set-total-questions-dropdown">N (Total questions): <span id="set-total-questions-value">5</span></label>
            <select id="set-total-questions-dropdown">
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5" selected>5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10</option>
            </select>
          </div>
          <div class="slider-wrap">
            <label for="set-min-correct-slider">K (Minimum correct required): <span id="set-min-correct-value">3</span></label>
            <div class="range-slider">
              <div class="range-slider-track" aria-hidden="true">
                <div class="range-slider-fill" id="set-min-correct-slider-fill"></div>
              </div>
              <div class="range-slider-marks" id="set-min-correct-slider-marks" aria-hidden="true"></div>
              <input type="range" id="set-min-correct-slider" min="3" max="10" value="3" step="1">
            </div>
          </div>
          </div>
          <div class="flex-column" id="set-security-questions-list"></div>
        </fieldset>
      </form>
      ${enableFaceRecognition
      ? '<label class="enable-fr-wrap"><input class="enable-fr" type="checkbox" value="true" id="registerFr">Register Face</label>'
      : ""
    }
      <div class="dialog-actions">
        <button type="button" id="set-security-questions-ok">Ok</button>
      </div>
    </div>
  `;

  document.body.appendChild(passphraseDialog);
  document.body.appendChild(securityQuestionsDialog);

  const totalQuestionsDropdown = securityQuestionsDialog.querySelector(
    "#set-total-questions-dropdown",
  );
  const minCorrectSlider = securityQuestionsDialog.querySelector(
    "#set-min-correct-slider",
  );
  const minCorrectSliderMarks = securityQuestionsDialog.querySelector(
    "#set-min-correct-slider-marks",
  );
  const minCorrectSliderFill = securityQuestionsDialog.querySelector(
    "#set-min-correct-slider-fill",
  );
  const totalQuestionsValue = securityQuestionsDialog.querySelector(
    "#set-total-questions-value",
  );
  const minCorrectValue = securityQuestionsDialog.querySelector(
    "#set-min-correct-value",
  );
  const forgettingAnswersValue = securityQuestionsDialog.querySelector(
    "#set-forgetting-answers-value",
  );
  const securityQuestionsList = securityQuestionsDialog.querySelector(
    "#set-security-questions-list",
  );
  bindFormSubmitToDefaultAction(passphraseDialog, "#set-passphrase-ok");
  bindFormSubmitToDefaultAction(
    securityQuestionsDialog,
    "#set-security-questions-ok",
  );

  let renderedQuestionCount = 0;

  const escapeInputAttributeValue = escapeHtml;

  const captureSecurityQuestionValues = () => {
    const values = [];
    for (let i = 0; i < renderedQuestionCount; i++) {
      const questionEl = document.getElementById("set-bayunSecQuesText" + i);
      const answerEl = document.getElementById("set-bayunSecQuesAnswer" + i);
      values.push({
        question: questionEl?.value ?? "",
        answer: answerEl?.value ?? "",
      });
    }
    return values;
  };

  const updateRangeSliderFill = (fillEl, value, min, max) => {
    if (!fillEl) {
      return;
    }
    const minVal = Number(min);
    const maxVal = Number(max);
    const currentVal = Number(value);
    if (
      !Number.isFinite(minVal) ||
      !Number.isFinite(maxVal) ||
      !Number.isFinite(currentVal) ||
      maxVal <= minVal
    ) {
      fillEl.style.width = "0%";
      return;
    }
    const fillPercent = ((currentVal - minVal) / (maxVal - minVal)) * 100;
    fillEl.style.width = `${fillPercent}%`;
  };

  const updateRangeSliderMarks = (marksEl, min, max) => {
    if (!marksEl) {
      return;
    }
    const minVal = Number(min);
    const maxVal = Number(max);
    if (!Number.isFinite(minVal) || !Number.isFinite(maxVal) || maxVal <= minVal + 1) {
      marksEl.innerHTML = "";
      return;
    }
    const span = maxVal - minVal;
    marksEl.innerHTML = Array.from(
      { length: maxVal - minVal - 1 },
      (_, index) => {
        const value = minVal + index + 1;
        const position = ((value - minVal) / span) * 100;
        return `<span class="range-slider-mark" style="left:${position}%"></span>`;
      },
    ).join("");
  };

  const renderSecurityQuestionInputs = (questionCount, preservedValues = []) => {
    if (!securityQuestionsList) {
      return;
    }
    securityQuestionsList.innerHTML = Array.from({ length: questionCount }, (_, i) => {
      const preserved = preservedValues[i] ?? { question: "", answer: "" };
      const questionValue = escapeInputAttributeValue(preserved.question);
      const answerValue = escapeInputAttributeValue(preserved.answer);
      return `<div id="set-bayunSecQuesBlock${i + 1}"><label for="set-bayunSecQuesText${i}">Question ${i + 1}</label><input name="bayunSecQues${i}" id="set-bayunSecQuesText${i}" placeholder="Question ${i + 1}" value="${questionValue}"><div class="answer-input-wrap"><input type="password" name="bayunSecQuesAnswer${i}" id="set-bayunSecQuesAnswer${i}" placeholder="Answer ${i + 1}" value="${answerValue}" autocomplete="off"><button type="button" class="sq-answer-visibility-toggle password-visibility-toggle" data-target-id="set-bayunSecQuesAnswer${i}" aria-label="Show answer ${i + 1}" aria-pressed="false">${getPasswordVisibilityToggleIconMarkup()}</button></div></div>`;
    }).join("");
    renderedQuestionCount = questionCount;
  };

  bindPasswordVisibilityToggle(passphraseDialog);
  bindPasswordVisibilityToggle(securityQuestionsDialog);

  const syncThresholdControls = () => {
    const nextN = Number(totalQuestionsDropdown?.value || 5);
    selectedK = Number(minCorrectSlider?.value || 3);
    const minimumAllowedK = getMinimumAllowedCorrectQuestions(nextN);
    if (minCorrectSlider) {
      minCorrectSlider.min = String(minimumAllowedK);
      minCorrectSlider.max = String(nextN);
      if (selectedK < minimumAllowedK) {
        selectedK = minimumAllowedK;
        minCorrectSlider.value = String(selectedK);
      } else if (selectedK > nextN) {
        selectedK = nextN;
        minCorrectSlider.value = String(selectedK);
      }
      updateRangeSliderMarks(
        minCorrectSliderMarks,
        minimumAllowedK,
        nextN,
      );
      updateRangeSliderFill(
        minCorrectSliderFill,
        selectedK,
        minimumAllowedK,
        nextN,
      );
    }
    selectedN = nextN;
    if (totalQuestionsValue) {
      totalQuestionsValue.textContent = String(selectedN);
    }
    if (minCorrectValue) {
      minCorrectValue.textContent = String(selectedK);
    }
    if (forgettingAnswersValue) {
      forgettingAnswersValue.textContent = String(selectedN - selectedK + 1);
    }
    if (selectedN !== renderedQuestionCount) {
      const preservedValues =
        renderedQuestionCount > 0 ? captureSecurityQuestionValues() : [];
      renderSecurityQuestionInputs(selectedN, preservedValues);
    }
  };

  totalQuestionsDropdown?.addEventListener("change", syncThresholdControls);
  minCorrectSlider?.addEventListener("input", syncThresholdControls);
  syncThresholdControls();

  const updateError = (errorMessage) => {
    const activeDialog = securityQuestionsDialog.open
      ? securityQuestionsDialog
      : passphraseDialog;
    const tips = activeDialog.querySelector(".validateTips");
    if (tips) {
      tips.classList.add("ui-state-error");
      tips.style.display = "block";
      tips.textContent = errorMessage;
    }
  };

  const removeError = () => {
    [securityQuestionsDialog, passphraseDialog].forEach((dialog) => {
      const tips = dialog.querySelector(".validateTips");
      if (tips) {
        tips.classList.remove("ui-state-error");
        tips.style.display = "none";
        tips.textContent = "";
      }
    });
  };

  const updateForPassphrase = () => {
    removeError();
    const passphraseInput = document.getElementById("set-bayunPassphrase");
    const passphraseConfirmInput = document.getElementById(
      "set-bayunPassphraseConfirm",
    );
    if (passphraseInput) {
      passphraseInput.value = "";
    }
    if (passphraseConfirmInput) {
      passphraseConfirmInput.value = "";
    }
    securityQuestionsDialog.close();
    passphraseDialog.showModal();
  };

  const isStrongPassphrase = (passphrase) => {
    if (passphrase.length < 8) {
      return false;
    }
    const hasLetter = /[A-Za-z]/.test(passphrase);
    const hasDigit = /\d/.test(passphrase);
    return hasLetter && hasDigit;
  };

  const getSecurityQuestionValidationError = (items, totalQuestions, minRequired) => {
    if (!isValidSecurityQuestionKNValues(totalQuestions, minRequired)) {
      return "Invalid security question configuration";
    }

    if (items.length < totalQuestions) {
      return `Please enter all ${totalQuestions} questions and their answers`;
    }

    // for (const item of items) {
    //   if (item.question.length < 5) {
    //     return "Each question must be at least 5 characters long";
    //   }
    //   if (item.answer.length < 2) {
    //     return "Each answer must be at least 2 characters long";
    //   }
    // }

    const normalizedQuestions = new Set();
    const normalizedAnswers = new Set();

    for (const item of items) {
      const normalizedQuestion = item.question.toLowerCase();
      const normalizedAnswer = item.answer.toLowerCase();

      // if (normalizedQuestions.has(normalizedQuestion)) {
      //   return "Security questions must be unique";
      // }
      // if (normalizedAnswers.has(normalizedAnswer)) {
      //   return "Security answers must be unique";
      // }

      normalizedQuestions.add(normalizedQuestion);
      normalizedAnswers.add(normalizedAnswer);
    }

    return null;
  };

  const setPassphraseSkipButton = passphraseDialog.querySelector(
    "#set-passphrase-skip",
  );
  const setPassphraseOkButton = passphraseDialog.querySelector("#set-passphrase-ok");
  let dismissOnlyMode = false;

  const closeCredentialSetupDialogs = () => {
    passphraseDialog.close();
    securityQuestionsDialog.close();
    passphraseDialog.remove();
    securityQuestionsDialog.remove();
  };

  const enterDismissOnlyMode = () => {
    dismissOnlyMode = true;
    if (setPassphraseSkipButton) {
      setPassphraseSkipButton.textContent = "Close";
      setPassphraseSkipButton.disabled = false;
      setPassphraseSkipButton.classList.remove("is-loading");
      setPassphraseSkipButton.removeAttribute("aria-busy");
    }
    if (setPassphraseOkButton) {
      setPassphraseOkButton.disabled = true;
      setPassphraseOkButton.classList.remove("is-loading");
      setPassphraseOkButton.removeAttribute("aria-busy");
      setPassphraseOkButton.style.display = "none";
    }
  };

  const handleCredentialSetupFailure = (error) => {
    updateError(getFailureMessage(error));
    if (isNonRetryableCredentialSetupError(error)) {
      enterDismissOnlyMode();
    }
  };

  passphraseDialog.addEventListener("cancel", (event) => {
    if (dismissOnlyMode) {
      event.preventDefault();
      closeCredentialSetupDialogs();
    }
  });

  setPassphraseSkipButton?.addEventListener("click", async () => {
    if (dismissOnlyMode) {
      closeCredentialSetupDialogs();
      return;
    }
    if (setPassphraseOkButton?.classList.contains("is-loading")) {
      return;
    }

    let setupFailed = false;
    setDialogButtonLoading(setPassphraseSkipButton, true, setPassphraseOkButton);
    try {
      await bayunCore.setNewUserCredentials({
        sessionId: sessionId,
        securityQuestions: answersList,
        minimumCorrectQuestions: selectedK,
        passphrase: "",
        registerFaceId: registerFaceId,
        authorizeMemberCallback: authorizeMemberCallback,
        successCallback: successCallback,
        failureCallback: wrapFailureCallback(failureCallback, (error) => {
          setupFailed = true;
          handleCredentialSetupFailure(error);
        }),
      });
    } catch (error) {
      if (!setupFailed) {
        setupFailed = true;
        handleCredentialSetupFailure(error);
        failureCallback(error);
      }
    } finally {
      if (!dismissOnlyMode) {
        setDialogButtonLoading(setPassphraseSkipButton, false, setPassphraseOkButton);
      }
    }
    if (!setupFailed) {
      closeCredentialSetupDialogs();
    }
  });

  setPassphraseOkButton?.addEventListener("click", async () => {
    if (dismissOnlyMode) {
      closeCredentialSetupDialogs();
      return;
    }
    removeError();
    const passphrase = document
      .getElementById("set-bayunPassphrase")
      ?.value.trim();
    const confirmPassphrase = document
      .getElementById(
        "set-bayunPassphraseConfirm",
      )
      ?.value.trim();

    if (!passphrase || !confirmPassphrase) {
      updateError("Passphrase cannot be empty");
    } else if (passphrase !== confirmPassphrase) {
      updateError("The passphrase does not match");
    }
    // else if (!isStrongPassphrase(passphrase)) {
    //   updateError(
    //     "Passphrase must be at least 8 characters and include letters and numbers",
    //   );
    // }
    else {
      let setupFailed = false;
      setDialogButtonLoading(setPassphraseOkButton, true, setPassphraseSkipButton);
      try {
        await bayunCore.setNewUserCredentials({
          sessionId: sessionId,
          securityQuestions: answersList,
          minimumCorrectQuestions: selectedK,
          passphrase: passphrase,
          registerFaceId: registerFaceId,
          authorizeMemberCallback: authorizeMemberCallback,
          successCallback: successCallback,
          failureCallback: wrapFailureCallback(failureCallback, (error) => {
            setupFailed = true;
            handleCredentialSetupFailure(error);
          }),
        });
      } catch (error) {
        if (!setupFailed) {
          setupFailed = true;
          handleCredentialSetupFailure(error);
          failureCallback(error);
        }
      } finally {
        if (!dismissOnlyMode) {
          setDialogButtonLoading(setPassphraseOkButton, false, setPassphraseSkipButton);
        }
      }

      if (!setupFailed) {
        closeCredentialSetupDialogs();
      }
    }
  });

  securityQuestionsDialog
    .querySelector("#set-security-questions-ok")
    ?.addEventListener("click", () => {
      removeError();
      answersList = [];
      for (let i = 0; i < selectedN; i++) {
        const questionEl = document.getElementById("set-bayunSecQuesText" + i);
        const answerEl = document.getElementById("set-bayunSecQuesAnswer" + i);

        const questionValue = questionEl?.value.trim();
        const answerValue = answerEl?.value.trim();
        if (questionValue && answerValue) {
          answersList.push({
            question: questionValue,
            answer: answerValue,
          });
        }
      }

      const sqValidationError = getSecurityQuestionValidationError(
        answersList,
        selectedN,
        selectedK,
      );
      if (sqValidationError) {
        updateError(sqValidationError);
      }

      if (enableFaceRecognition) {
        const registerFr = document.getElementById("registerFr")?.checked || false;
        registerFaceId = registerFr;
      } else {
        registerFaceId = false;
      }

      if (!sqValidationError) {
        updateForPassphrase();
      }
    });

  securityQuestionsDialog.showModal();
}
